import sleep from "sleep-promise";
import { beaconFail } from "./assertions.js";
const DEFAULT_TIMEOUT = 5000;
function isTextNode(node) {
    return node.nodeType === node.TEXT_NODE;
}
function isHtmlElement(node) {
    return node.nodeType === node.ELEMENT_NODE;
}
function* exponentialBackoff(timeout) {
    let delay = 1;
    let total = 0;
    while (total < timeout) {
        yield delay;
        total += delay;
        delay = Math.min(delay * 2, 1000);
    }
}
async function retryWithBackoff(thunk, timeout) {
    for (const delay of exponentialBackoff(timeout)) {
        const result = thunk();
        if (result !== false) {
            return result;
        }
        await sleep(delay);
    }
}
export const DomUtils = {
    querySelectorOrNull(selector, on) {
        const found = (on ?? document).querySelectorAll(selector);
        if (found.length == 0) {
            return null;
        }
        if (found.length > 1) {
            beaconFail(`Multiple elements found for selector '${selector}'`);
        }
        return found[0];
    },
    querySelectorOrFail(selector, on) {
        return DomUtils.querySelectorOrNull(selector, on) ?? beaconFail(`Element not found for selector '${selector}'`);
    },
    async waitForQuerySelectorOrFail(selector, on, options = {}) {
        const timeout = options.timeout ?? DEFAULT_TIMEOUT;
        return ((await retryWithBackoff(() => {
            const found = DomUtils.querySelectorOrNull(selector, on);
            if (found != null) {
                return found;
            }
            else {
                return false;
            }
        }, timeout)) ?? beaconFail(`Element not found for selector '${selector}' after waiting for ${timeout}ms`));
    },
    querySelectorAllOrNull(selector, on) {
        const found = (on ?? document).querySelectorAll(selector);
        if (found.length == 0) {
            return null;
        }
        return [...found];
    },
    querySelectorAllOrFail(selector, on) {
        return (DomUtils.querySelectorAllOrNull(selector, on) ?? beaconFail(`No element not found for selector '${selector}'`));
    },
    async waitForQuerySelectorAllOrFail(selector, on, options = {}) {
        const timeout = options.timeout ?? DEFAULT_TIMEOUT;
        return [
            ...((await retryWithBackoff(() => {
                const found = DomUtils.querySelectorAllOrNull(selector, on);
                if (found != null) {
                    return found;
                }
                else {
                    return false;
                }
            }, timeout)) ?? beaconFail(`Element(s) not found for selector '${selector}' after waiting for ${timeout}ms`)),
        ];
    },
    textContentOrNull(selector, on) {
        const found = DomUtils.querySelectorOrNull(selector, on);
        if (found == null) {
            return null;
        }
        return found.textContent;
    },
    textContentOrFail(selector, on) {
        return DomUtils.querySelectorOrFail(selector, on).textContent ?? "";
    },
    waitForTextContentOrFail(selector, on, options = {}) {
        return DomUtils.waitForQuerySelectorOrFail(selector, on, options).then((element) => element.textContent ?? "");
    },
    closestOrNull(selector, on) {
        return on.closest(selector);
    },
    closestOrFail(selector, on) {
        return (DomUtils.closestOrNull(selector, on) ??
            beaconFail(`Element not found for selector '${selector}' and closest()`));
    },
    extractNonStrikeOutText(node) {
        if (isTextNode(node)) {
            return node.textContent ?? "";
        }
        else if (isHtmlElement(node)) {
            const computedTextDecoration = node.ownerDocument.defaultView?.getComputedStyle(node).textDecoration ?? "";
            if (computedTextDecoration.includes("strike-through")) {
                return "";
            }
            if (computedTextDecoration.includes("line-through")) {
                return "";
            }
            let text = "";
            for (const child of node.childNodes) {
                text += DomUtils.extractNonStrikeOutText(child);
            }
            return text;
        }
        else {
            return "";
        }
    },
};
//# sourceMappingURL=dom-utils.js.map